import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { RELEASE_URL } from '../../config/env';
import { MENU_ROUTES } from '../../constants';
import {
  Button,
  IconArrowRight as ArrowIcon,
  IconGrid as Grid,
  IconX as XIcon,
  IconNavOverview as IconOverview,
  IconNavDesignSystem as IconDesignSystem,
  IconNavBuilder as IconBuilder,
} from '../../core';
import useDisableBodyScroll from '../../hooks/useDisableBodyScroll';
import { ReactComponent as Logo } from '../../images/dhcs.svg';

type HeaderNavigationMobileProps = {
  className?: string;
};

const HeaderNavigationMobile = ({ className = '' }: HeaderNavigationMobileProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Custom hook to disable body scroll when the menu is open
  useDisableBodyScroll(isMenuOpen);

  return (
    <div className={className}>
      {isMenuOpen && <div className="menu-overlay" />}
      <div className="menu-bar">
        <Link to={MENU_ROUTES.overview.path}>
          <Logo />
        </Link>
        <div className="menu-bar__actions">
          <Button onClick={() => (window.location.href = RELEASE_URL)} variant="primary" size="S">
            Builder
          </Button>

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="toggle-menu-btn"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <XIcon /> : <Grid />}
          </button>
        </div>
      </div>

      <div className={`menu-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="menu-items">
          <ul>
            <li>
              <NavLink
                to={MENU_ROUTES.overview.path}
                className={({ isActive }) =>
                  isActive ? 'menu-link menu-link--active' : 'menu-link'
                }
              >
                <IconOverview />
                <span>{MENU_ROUTES.overview.name}</span>
                <ArrowIcon />
              </NavLink>
            </li>
            <li>
              <NavLink
                to={MENU_ROUTES.builder.path}
                className={({ isActive }) =>
                  isActive ? 'menu-link menu-link--active' : 'menu-link'
                }
              >
                <IconBuilder />
                <span>{MENU_ROUTES.builder.name}</span>
                <ArrowIcon />
              </NavLink>
            </li>
            <li>
              <NavLink
                to={MENU_ROUTES.designSystem.path}
                className={({ isActive }) =>
                  isActive ? 'menu-link menu-link--active' : 'menu-link'
                }
              >
                <IconDesignSystem />
                <span>{MENU_ROUTES.designSystem.name}</span>
                <ArrowIcon />
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const HeaderNavigationMobileStyled = styled(HeaderNavigationMobile)`
  display: block;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.neutralBackgroundMedium};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }

  > .menu-overlay {
    background-color: ${({ theme }) => theme.neutralScrim};
    position: absolute;
    width: 100%;
    height: calc(100vh - 48px);
    left: 0;
    top: 48px;
    z-index: 1;
  }

  > .menu-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;

    > .menu-bar__actions {
      display: flex;
      gap: 14px;
      position: relative;

      > .toggle-menu-btn {
        border: unset;
        display: flex;
        align-items: center;
        position: relative;
        margin-left: 14px;
        cursor: pointer;

        &::before {
          position: absolute;
          content: '';
          display: block;
          width: 1px;
          height: 48px;
          background-color: ${({ theme }) => theme.neutralBorder};
          left: -14px;
        }
      }
    }
  }

  > .menu-drawer {
    z-index: 11;
    padding: 20px 12px;
    background-color: ${({ theme }) => theme.neutralBackgroundMedium};
    border-top: 1px solid ${({ theme }) => theme.neutralBorder};
    position: fixed;
    top: 48px;
    right: 0;
    height: calc(100vh - 48px);
    width: 272px;
    transition: transform 0.3s ease-in-out;
    transform: translateX(100%);

    &.open {
      transform: translateX(0%);
    }

    > .menu-items {
      > ul {
        list-style: none;

        > li {
          > .menu-link {
            text-decoration: none;
            color: ${({ theme }) => theme.neutralTextWeak};
            width: 100%;
            padding: 12px 16px;
            font-size: ${({ theme }) => theme.fontSizes.sm};
            line-height: ${({ theme }) => theme.lineHeights.sm};
            display: flex;
            align-items: center;

            &--active {
              color: ${({ theme }) => theme.primaryBackgroundStrong};
              background-color: ${({ theme }) => theme.neutralBackgroundBase};
              border-radius: 6px;

              > svg:last-child {
                display: none;
              }
            }

            > svg:first-child {
              margin-right: 8px;
            }
            > svg:last-child {
              margin-left: auto;
            }
          }
        }
      }
    }

    > .user-profile {
      position: absolute;
      bottom: 0;
    }
  }
`;

export default HeaderNavigationMobileStyled;
